import React, { useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { getPlayer, type PlayerProfileData, type RecentBet } from './adminApi';
import { useAdminAccess } from './adminAccess';
import { usePolling } from './usePolling';
import { formatCount, formatCredits, formatPercent, formatPlatform, formatSignedCredits, formatUtc } from './format';
import { SlowBanner, Unavailable } from './PanelState';

const PROVIDER_LABEL: Record<string, string> = { password: 'Password', google: 'Google', apple: 'Apple' };
const ROLE_LABEL: Record<string, string> = { player: 'Player', admin: 'Admin' };
const BLANK = '—';

const Field: React.FC<{ label: string; children: React.ReactNode; className?: string }> = ({ label, children, className }) => (
  <div className={className}><dt>{label}</dt><dd>{children}</dd></div>
);

function exitLabel(bet: RecentBet) {
  if (bet.status === 'lost') return 'Lost';
  if (bet.status === 'cashed_out') return bet.cashoutMultiplier ? `${bet.cashoutMultiplier.toFixed(2)}×` : 'Cashed out';
  if (bet.status === 'cancelled' || bet.status === 'refunded') return 'Refunded';
  return 'Live';
}

const days = (count: number) => `${formatCount(count)} ${count === 1 ? 'day' : 'days'}`;

/** One player's detail (spec US3). Email arrives masked; addresses, hashes and tokens never arrive. */
const PlayerProfile: React.FC<{ id: string; onBack: () => void }> = ({ id, onBack }) => {
  const { token } = useAuth();
  const report = useAdminAccess();
  const fetcher = useCallback((signal: AbortSignal) => getPlayer(token, id, signal), [token, id]);
  const { data, error, refresh } = usePolling<PlayerProfileData>(fetcher, null, { onError: report });

  const back = <button type="button" className="admin-button" onClick={onBack}>← Back to list</button>;

  if (error?.kind === 'notfound') {
    return <div className="admin-profile">{back}<p className="admin-empty">Player not found.</p></div>;
  }
  if (error && error.kind !== 'signin' && error.kind !== 'forbidden') {
    return (
      <div className="admin-profile">
        {back}
        <SlowBanner error={error} />
        <Unavailable message="Profile unavailable" onRetry={refresh} />
      </div>
    );
  }
  if (!data) return <div className="admin-profile">{back}<p className="admin-muted">Loading player…</p></div>;

  const { profile, sessions, stats, recentBets } = data;
  // The server already anonymises deleted accounts; never show personal fields for one regardless.
  const deleted = Boolean(profile.deletedAt);
  const username = deleted ? 'Deleted player' : profile.username;
  const providers = deleted ? [] : profile.providers;
  const net = Number(stats.net);

  return (
    <div className="admin-profile">
      <div className="admin-profile-head">
        {back}
        <h2>{username}</h2>
        <span className={`admin-pill ${deleted ? 'is-deleted' : profile.isActive ? 'is-active' : 'is-inactive'}`}>
          {deleted ? 'Deleted' : profile.isActive ? 'Active' : 'Inactive'}
        </span>
      </div>

      <div className="admin-profile-grid">
        <section className="admin-card" aria-labelledby="profile-identity">
          <h3 id="profile-identity" className="section-label">Identity</h3>
          <dl className="admin-fields">
            <Field label="Display name">{(!deleted && profile.displayName) || BLANK}</Field>
            <Field label="Email">{(!deleted && profile.emailMasked) || BLANK}</Field>
            <Field label="Sign-in methods">{providers.length ? providers.map(p => PROVIDER_LABEL[p] ?? p).join(', ') : BLANK}</Field>
            <Field label="Role">{ROLE_LABEL[profile.role] ?? profile.role}</Field>
            <Field label="Joined">{formatUtc(profile.createdAt)}</Field>
            <Field label="Last seen">{formatUtc(profile.lastSeenAt ?? sessions.lastSeenAt ?? null)}</Field>
            <Field label="Account">{deleted ? 'Deleted' : profile.isActive ? 'Active' : 'Inactive'}</Field>
          </dl>
        </section>

        <section className="admin-card" aria-labelledby="profile-sessions">
          <h3 id="profile-sessions" className="section-label">Sessions</h3>
          <dl className="admin-fields">
            <Field label="Sessions">{formatCount(sessions.count)}</Field>
            <Field label="Average session">{sessions.avgMinutes ? `${sessions.avgMinutes} min` : BLANK}</Field>
            <Field label="Last platform">{formatPlatform(sessions.lastPlatform)}</Field>
            <Field label="Last app version">{sessions.lastAppVersion || BLANK}</Field>
          </dl>
        </section>
      </div>

      <section className="admin-card" aria-labelledby="profile-stats">
        <h3 id="profile-stats" className="section-label">Statistics</h3>
        <dl className="admin-fields admin-fields-grid">
          <Field label="Rounds">{formatCount(stats.roundsPlayed)}</Field>
          <Field label="Cashed out">{formatPercent(stats.cashoutRate)}</Field>
          <Field label="Wagered">{formatCredits(stats.wagered)}</Field>
          <Field label="Net" className={net > 0 ? 'stat-up' : net < 0 ? 'stat-down' : undefined}>{formatSignedCredits(stats.net)}</Field>
          <Field label="Average wager">{formatCredits(stats.avgBet)}</Field>
          <Field label="Average exit">{stats.avgCashoutMultiplier ? `${stats.avgCashoutMultiplier.toFixed(2)}×` : BLANK}</Field>
          {/* With no cash-outs the "best" result is just the smallest loss; that is not a win. */}
          <Field label="Best win">{stats.cashouts > 0 ? formatCredits(stats.biggestWin) : BLANK}</Field>
          <Field label="Biggest loss">{formatSignedCredits(stats.biggestLoss)}</Field>
          <Field label="Automatic exits">{formatPercent(stats.autoCashoutShare)}</Field>
          <Field label="Days played">{formatCount(stats.daysPlayed)}</Field>
          <Field label="Current streak">{days(stats.currentStreakDays)}</Field>
          <Field label="Longest streak">{days(stats.longestStreakDays)}</Field>
        </dl>
        <p className="admin-muted">Amounts are play credits.</p>
      </section>

      <section className="admin-card" aria-labelledby="profile-bets">
        <h3 id="profile-bets" className="section-label">Recent bets</h3>
        {recentBets.length === 0 ? <p className="admin-empty">No bets yet.</p> : (
          <div className="admin-table-wrap">
            <table className="admin-table">
              <caption className="sr-only">Recent bets</caption>
              <thead>
                <tr>
                  <th scope="col">Time</th>
                  <th scope="col">Round</th>
                  <th scope="col" className="num">Amount</th>
                  <th scope="col" className="num">Cash-out</th>
                  <th scope="col" className="num">Net</th>
                </tr>
              </thead>
              <tbody>
                {recentBets.map(bet => {
                  const profit = Number(bet.profit);
                  return (
                    <tr key={bet.id}>
                      <td>{formatUtc(bet.placedAt)}</td>
                      <td>{bet.gameNumber === null ? BLANK : `#${bet.gameNumber}`}</td>
                      <td className="num">{formatCredits(bet.amount)}</td>
                      <td className="num">{exitLabel(bet)}</td>
                      <td className={`num ${profit > 0 ? 'stat-up' : profit < 0 ? 'stat-down' : ''}`}>{formatSignedCredits(bet.profit)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </div>
  );
};

export default PlayerProfile;
